import { ERROR_CATEGORY } from '@sky-distribution/adapter-core-pc-rest-interfaces';
import { DigitalGpSubmission, Variants } from '@sky-distribution/digital-gp-submission';
import { getUserInfo } from '@sky-distribution/mf-authorization';
import { get as lodashGet } from 'lodash';
import { Guard, StateValue, StateValueMap } from 'xstate';
import { isErrorPlatformEvent } from '../events';
import { AppState, AppStateMachineContext, AppStateMachineEvent } from '../types';

const INVALID_OTP_CODE_MESSAGE = 'INVALID_OTP_CODE';

const isStateValueMap = (value: StateValue): value is StateValueMap => typeof value !== 'string';

export function isPolicyInState(state: AppState, stateName: string): boolean {
  const { value } = state;

  return isStateValueMap(value) && value.policy === stateName;
}

export const cantInsure = (event: AppStateMachineEvent): boolean => {
  if (isErrorPlatformEvent(event) && event.data?.isAxiosError && event.data?.response) {
    const { message } = event.data.response.data;

    return message === ERROR_CATEGORY.UW_ISSUE;
  }

  return false;
};

export const earlyRenewalError = (event: AppStateMachineEvent): boolean => {
  if (isErrorPlatformEvent(event) && event.data?.isAxiosError && event.data?.response) {
    const { message } = event.data.response.data;

    return message === ERROR_CATEGORY.EARLY_RENEWAL_ERROR;
  }

  return false;
};

export const lateRenewalError = (event: AppStateMachineEvent): boolean => {
  if (isErrorPlatformEvent(event) && event.data?.isAxiosError && event.data?.response) {
    const { message } = event.data.response.data;

    return message === ERROR_CATEGORY.LATE_RENEWAL_ERROR;
  }

  return false;
};

export const isUnauthorized = (event: AppStateMachineEvent): boolean => {
  if (isErrorPlatformEvent(event) && event.data?.isAxiosError && event.data?.response) {
    return event.data.response.status === 401;
  }

  return false;
};

export const isInvalidOtp = (event: AppStateMachineEvent): boolean => {
  if (isErrorPlatformEvent(event) && event.data?.isAxiosError && event.data?.response) {
    const { message } = event.data.response.data;

    return message === INVALID_OTP_CODE_MESSAGE;
  }

  return false;
};

export const isLoggedIn = () => !!getUserInfo();

export const isContextPropertyEqual = (
  context: AppStateMachineContext, cond: Guard<AppStateMachineContext, AppStateMachineEvent>,
) => {
  if ('property' in cond && 'expected' in cond) {
    return lodashGet(context, cond.property) === cond.expected;
  }

  return false;
};

export const searchParamExist = (
  context: AppStateMachineContext,
  cond: Guard<AppStateMachineContext, AppStateMachineEvent>,
) => {
  if ('property' in cond) {
    return !!lodashGet(context, cond.property);
  }

  return false;
};

const hasCoverageFromList = (
  submission: DigitalGpSubmission, coverageCodes: string[],
) => (submission?.variants as Variants | undefined)?.[0]?.coverages
  ?.some(({
    coverageCode,
  }) => coverageCodes.includes(coverageCode));

export const hasCoverage = (
  context: AppStateMachineContext,
  cond: Guard<AppStateMachineContext, AppStateMachineEvent>,
): boolean => 'coverageCodes' in cond && !!hasCoverageFromList(context.digitalSubmission, cond.coverageCodes);
